import React, { useState, useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { dbHelpers } from '../../db/database';
import { ordersAPI } from '../../api/orders';
import './OrderDetail.css';
import { useToast } from '../../hooks/useToast';
import { useModal } from '../../hooks/useModal';
import Modal from '../common/Modal';


function OrderDetail() {
  const navigate = useNavigate();
  const { id } = useParams();
  const [order, setOrder] = useState(null);
  const [orderItems, setOrderItems] = useState([]);
  const [customer, setCustomer] = useState(null);
  const [warehouse, setWarehouse] = useState(null);
  const [loading, setLoading] = useState(true);
  const [updating, setUpdating] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const toast = useToast();
  const { isOpen, modalConfig, openModal, closeModal } = useModal();


  useEffect(() => {
    loadOrderDetails();
  }, [id]);

  async function loadOrderDetails() {
    setLoading(true);
    try {
      // Fetch from API to get latest data
      const response = await ordersAPI.getById(id);
      const orderData = response.data;

      setOrder(orderData);

      // Load customer info
      if (orderData.customer) {
        const customerData = await dbHelpers.getCustomerById(orderData.customer);
        setCustomer(customerData);
      }

      // Load warehouse info
      if (orderData.warehouse) {
        const warehouseData = await dbHelpers.getWarehouseById(orderData.warehouse);
        setWarehouse(warehouseData);
      }

      // Set items from API response
      if (orderData.items && orderData.items.length > 0) {
        setOrderItems(orderData.items);
      } else {
        // Fallback to IndexedDB
        const items = await dbHelpers.getOrderItems(orderData.id);
        const itemsWithProducts = await Promise.all(
          items.map(async (item) => {
            const product = await dbHelpers.getProductById(item.product_id || item.product);
            return {
              ...item,
              product_name: product?.name || 'Unknown Product',
              product_sku: product?.sku || 'N/A'
            };
          })
        );
        setOrderItems(itemsWithProducts);
      }
    } catch (error) {

      // Check if order was deleted
      if (error.response?.status === 404) {
        setError('Order not found. It may have been deleted.');
        navigate('/orders');
      } else {
        setError('Failed to load order details');
      }
    }
    setLoading(false);
  }


  async function performStatusChange(newStatus) {
  if (!order) return;
  
  setUpdating(true);
  setError('');  
  setSuccess(''); 
  
  try {
    const response = await ordersAPI.updateStatus(id, newStatus);
    setOrder(response.data);
    await dbHelpers.saveOrders([response.data]);
    
    const successMessages = {
      confirmed: '✅ Order confirmed! Stock has been deducted.',
      cancelled: '✅ Order cancelled! Stock has been restored.',
      delivered: '✅ Order marked as delivered!',
      shipped: '✅ Order marked as shipped!',
      processing: '✅ Order is now processing!',
      pending: '✅ Order changed to pending!'
    };
    
    const successMsg = successMessages[newStatus] || '✅ Order status updated!';
    setSuccess(successMsg);
    
    if (toast) {
      toast.success(successMsg);
    }
    
    await loadOrderDetails();
    
    // Clear success after 5 seconds
    setTimeout(() => setSuccess(''), 5000);
    
    closeModal();
    
  } catch (error) {
   
    let errorMsg = 'Failed to update order status';
    
    if (error.response?.data) {
      if (typeof error.response.data === 'object') {
        errorMsg = error.response.data.error || JSON.stringify(error.response.data);
      } else {
        errorMsg = error.response.data;
      }
    }
    
    setError(errorMsg);

    closeModal();
  }
  
  setUpdating(false);
}

  function handleStatusChange(newStatus) {
    if (!order) return;

    const statusMessages = {
      confirmed: 'Confirm this order? Stock will be deducted.',
      cancelled: 'Cancel this order? Stock will be restored if it was confirmed.',
      delivered: 'Mark this order as delivered?',
      shipped: 'Mark this order as shipped?',
      processing: 'Mark this order as processing?',
      pending: 'Change order back to pending?'
    };

    const confirmMessage = statusMessages[newStatus] || `Change status to ${newStatus}?`;

    const statusTitles = {
      confirmed: 'Confirm Order?',
      cancelled: 'Cancel Order?',
      delivered: 'Mark as Delivered?',
      shipped: 'Mark as Shipped?',
      processing: 'Mark as Processing?',
      pending: 'Change to Pending?'
    };

    const confirmButtonText = {
      confirmed: 'Confirm Order',
      cancelled: 'Cancel Order',
      delivered: 'Mark Delivered',
      shipped: 'Mark Shipped',
      processing: 'Mark Processing',
      pending: 'Change to Pending'
    };

    openModal({
      title: statusTitles[newStatus] || 'Change Order Status?',
      message: confirmMessage,
      type: newStatus === 'cancelled' ? 'danger' : 'warning',
      confirmText: confirmButtonText[newStatus] || 'Confirm',
      cancelText: 'Cancel',
      onConfirm: () => performStatusChange(newStatus)
    });
  }

  function getStatusColor(status) {
    const colors = {
      pending: '#f59e0b',
      confirmed: '#3b82f6',
      processing: '#3b82f6',
      shipped: '#10b981',
      delivered: '#059669',
      cancelled: '#ef4444'
    };
    return colors[status] || '#6b7280';
  }

  function getStatusLabel(status) {
    const labels = {
      pending: 'Pending',
      confirmed: 'Confirmed',
      processing: 'Processing',
      shipped: 'Shipped',
      delivered: 'Delivered',
      cancelled: 'Cancelled'
    };
    return labels[status] || status;
  }

  function formatDate(dateString) {
    if (!dateString) return 'N/A';
    const date = new Date(dateString);
    return date.toLocaleDateString('en-GB', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric'
    });
  }

  function handleDuplicateOrder() {
  navigate('/orders/new', {
    state: {
      duplicateFrom: order.id,
      customer: order.customer,
      warehouse: order.warehouse,
      items: order.items
    }
  });
}

  if (loading) {
    return <div className="loading-screen">Loading order details...</div>;
  }

  if (!order) {
    return <div className="error-screen">Order not found</div>;
  }

  const isLocked = order.status === 'confirmed' || order.status === 'delivered';

  return (
    <div className="order-detail-container fade-in">
      <div className="breadcrumb">
        <button onClick={() => navigate('/orders')} className="btn-primary-small">
          ← Back to Orders
        </button>
      </div>

      {error && (
  <div style={{
    padding: '0',
    margin: '20px 0',
    backgroundColor: '#fff',
    border: '1px solid #f87171',
    borderRadius: '8px',
    overflow: 'hidden',
    boxShadow: '0 4px 12px rgba(239, 68, 68, 0.15)',
    position: 'relative'
  }}>
    {/* Red header bar */}
    <div style={{
      background: 'linear-gradient(135deg, #dc2626 0%, #991b1b 100%)',
      padding: '12px 20px',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'space-between'
    }}>
      <div style={{ display: 'flex', alignItems: 'center' }}>
        <span style={{ fontSize: '20px', marginRight: '10px' }}>⚠️</span>
        <span style={{
          color: 'white',
          fontSize: '15px',
          fontWeight: '600',
          letterSpacing: '0.5px'
        }}>
          STOCK VALIDATION ERROR
        </span>
      </div>
      
      <button 
        onClick={() => setError('')}
        style={{
          background: 'rgba(255, 255, 255, 0.2)',
          border: 'none',
          color: 'white',
          fontSize: '18px',
          cursor: 'pointer',
          padding: '4px 10px',
          borderRadius: '4px',
          transition: 'background 0.2s'
        }}
        onMouseEnter={(e) => e.target.style.background = 'rgba(255, 255, 255, 0.3)'}
        onMouseLeave={(e) => e.target.style.background = 'rgba(255, 255, 255, 0.2)'}
        title="Dismiss"
      >
        ✕
      </button>
    </div>

    {/* White content area */}
    <div style={{
      padding: '20px 24px',
      color: '#450a0a',
      fontSize: '14px',
      lineHeight: '1.7',
      whiteSpace: 'pre-line',
      backgroundColor: '#fef2f2'
    }}>
      {error}
    </div>
  </div>
)}

      <div className="order-header">
        <div>
          <h1>Order #{String(order.id).slice(0, 8)}</h1>
          <p className="order-date">
            Created: {formatDate(order.created_at || order.order_date)}
          </p>
        </div>
        <div className="order-status-badge" style={{ background: getStatusColor(order.status) }}>
          {getStatusLabel(order.status).toUpperCase()}
        </div>
      </div>

      {/* Customer & Warehouse Info */}
      <div className="info-cards">
        <div className="info-card">
          <h3>👤 Customer Information</h3>
          {customer || order.customer_name ? (
            <>
              <div className="info-row">
                <span className="label">Name:</span>
                <span className="value">{customer?.name || order.customer_name}</span>
              </div>
              {customer?.phone && (
                <div className="info-row">
                  <span className="label">Phone:</span>
                  <span className="value">{customer.phone}</span>
                </div>
              )}
              {customer?.email && (
                <div className="info-row">
                  <span className="label">Email:</span>
                  <span className="value">{customer.email}</span>
                </div>
              )}
            </>
          ) : (
            <p className="no-data">Customer information not available</p>
          )}
        </div>

        <div className="info-card">
          <h3>🏭 Warehouse</h3>
          {warehouse || order.warehouse_name ? (
            <>
              <div className="info-row">
                <span className="label">Name:</span>
                <span className="value">{warehouse?.name || order.warehouse_name}</span>
              </div>
              {warehouse?.location && (
                <div className="info-row">
                  <span className="label">Location:</span>
                  <span className="value">{warehouse.location}</span>
                </div>
              )}
            </>
          ) : (
            <p className="no-data">Warehouse information not available</p>
          )}
        </div>
      </div>

      {/* Order Items */}
      <div className="order-items-section">
        <h3>📋 Order Items</h3>

        {orderItems && orderItems.length > 0 ? (
          <div className="items-table-container">
            <table className="items-table">
              <thead>
                <tr>
                  <th>Product</th>
                  <th>SKU</th>
                  <th>Quantity</th>
                  <th>Price</th>
                  <th>Subtotal</th>
                </tr>
              </thead>
              <tbody>
                {orderItems.map((item, index) => (
                  <tr key={item.id || index}>
                    <td>{item.product_name}</td>
                    <td>{item.product_sku}</td>
                    <td>{item.quantity}</td>
                    <td>TSh {parseFloat(item.price || 0).toFixed(2)}</td>
                    <td>TSh {parseFloat(item.subtotal || (item.quantity * item.price) || 0).toFixed(2)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        ) : (
          <p className="no-data">No items in this order</p>
        )}
      </div>

      {/* Notes */}
      {order.notes && (
        <div className="notes-section">
          <h3>📝 Notes</h3>
          <p>{order.notes}</p>
        </div>
      )}

      {/* Total */}
      <div className="order-total-section">
        <span className="total-label">Total Amount:</span>
        <span className="total-amount">TSh {parseFloat(order.total || 0).toFixed(2)}</span>
      </div>





<div className="status-actions">
  <h3>Change Order Status</h3>

  

  {/* ⭐ Warning for cancelled orders */}
  {order.status === 'cancelled' && (
    <div style={{
      padding: '12px 16px',
      backgroundColor: '#fef2f2',
      border: '1px solid #fca5a5',
      borderRadius: '6px',
      marginBottom: '16px',
      color: '#991b1b'
    }}>
      ⚠️ This order has been cancelled and cannot be reactivated. 
      To proceed with this order, please create a new one.
    </div>
  )}

  {order.status === 'delivered' && (
    <div style={{
      padding: '12px 16px',
      backgroundColor: '#eff6ff',
      // border: '1px solid '#93c5fd',
      borderRadius: '6px',
      marginBottom: '16px',
      color: '#1e40af'
    }}>
      ℹ️ This order has been delivered and is now final.
    </div>
  )}

  <div className="status-buttons">
    <button
      onClick={() => handleStatusChange('pending')}
      disabled={updating || order.status === 'pending' || order.status === 'cancelled' || order.status === 'delivered'}
      className="status-btn pending"
    >
      Pending
    </button>
    <button
      onClick={() => handleStatusChange('confirmed')}
      disabled={updating || order.status === 'confirmed' || order.status === 'cancelled' || order.status === 'delivered'}
      className="status-btn confirmed"
    >
      Confirmed
    </button>
    <button
      onClick={() => handleStatusChange('processing')}
      disabled={updating || order.status === 'processing' || order.status === 'cancelled' || order.status === 'delivered'}
      className="status-btn processing"
    >
      Processing
    </button>
    <button
      onClick={() => handleStatusChange('shipped')}
      disabled={updating || order.status === 'shipped' || order.status === 'cancelled' || order.status === 'delivered'}
      className="status-btn shipped"
    >
      Shipped
    </button>
    <button
      onClick={() => handleStatusChange('delivered')}
      disabled={updating || order.status === 'delivered' || order.status === 'cancelled'}
      className="status-btn delivered"
    >
      Delivered
    </button>
    <button
      onClick={() => handleStatusChange('cancelled')}
      disabled={updating || order.status === 'cancelled' || order.status === 'delivered'}
      className="status-btn cancelled"
    >
      Cancelled
    </button>
  </div>

  {/* ⭐ Duplicate Order button for cancelled orders */}
  {order.status === 'cancelled' && (
    <button
      onClick={handleDuplicateOrder}
      className="btn-primary"
      style={{ 
        marginTop: '16px',
        width: '100%',
        padding: '12px'
      }}
    >
      📋 Create New Order with Same Items
    </button>
  )}
</div>

<Modal
  isOpen={isOpen}
  onClose={closeModal}
  {...modalConfig}
/>




    </div>
  );
}

export default OrderDetail;
